import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes, HTMLAttributes, InputHTMLAttributes, ReactNode } from "react";
import { forwardRef, useEffect, useId, useRef } from "react";
import { cn } from "../lib/cn";

const buttonVariants = cva(
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-[10px] px-4 text-sm font-extrabold tracking-[-0.01em] transition-[transform,background-color,border-color,box-shadow,color] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "ui-hover-lift bg-primary text-primary-foreground shadow-[0_16px_32px_-18px_hsl(var(--primary))] hover:bg-primary/90 hover:shadow-[0_18px_38px_-18px_hsl(var(--primary))]",
        secondary:
          "ui-hover-lift bg-secondary text-secondary-foreground hover:bg-secondary/75",
        outline:
          "ui-hover-lift border border-border bg-card/60 text-foreground shadow-sm hover:border-primary/30 hover:bg-muted",
        ghost: "text-muted-foreground hover:bg-muted hover:text-foreground",
        danger:
          "ui-hover-lift bg-destructive text-white shadow-[0_12px_28px_-18px_hsl(var(--destructive))] hover:bg-destructive/90"
      },
      size: {
        default: "h-11 px-4",
        sm: "h-9 min-h-9 rounded-lg px-3 text-xs",
        icon: "h-11 w-11 px-0"
      }
    },
    defaultVariants: {
      variant: "primary",
      size: "default"
    }
  }
);

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
  )
);
Button.displayName = "Button";

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "observatory-panel rounded-xl text-card-foreground",
        className
      )}
      {...props}
    />
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        "flex h-12 w-full rounded-[10px] border border-input bg-background/55 px-3.5 text-sm text-foreground shadow-sm outline-none transition-[border-color,box-shadow,background-color] duration-150 placeholder:text-muted-foreground focus-visible:border-primary/70 focus-visible:bg-card focus-visible:ring-4 focus-visible:ring-ring/15 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    />
  )
);
Input.displayName = "Input";

export function Badge({
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-border/80 bg-muted/65 px-2.5 py-1 text-[11px] font-extrabold text-muted-foreground",
        className
      )}
      {...props}
    />
  );
}

// Banner dùng cho thông báo trạng thái có thể đọc được bằng screen reader.
export function StatusBanner({
  children,
  tone = "info",
  icon
}: {
  children: ReactNode;
  tone?: "info" | "success" | "warning" | "danger";
  icon?: ReactNode;
}) {
  const toneClass = {
    info: "border-primary/25 bg-primary/10 text-primary",
    success: "border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    warning: "border-accent/30 bg-accent/10 text-accent-foreground",
    danger: "border-destructive/30 bg-destructive/10 text-destructive"
  }[tone];
  return (
    <div className={cn("status-banner flex items-start gap-2 rounded-xl border px-4 py-3 text-sm font-bold", toneClass)} role={tone === "danger" ? "alert" : "status"}>
      {icon}
      <span>{children}</span>
    </div>
  );
}

// Skeleton tĩnh giúp cảm nhận tốc độ mà không tạo chuyển động dư thừa ở bảng dữ liệu.
export function LoadingSkeleton({ className = "" }: { className?: string }) {
  return <div className={cn("animate-pulse rounded-xl bg-muted", className)} aria-hidden="true" />;
}

// MetricCard giữ nhãn, số liệu và màu trạng thái nhất quán giữa dashboard và kết quả.
export function MetricCard({
  label,
  value,
  tone = "primary"
}: {
  label: string;
  value: ReactNode;
  tone?: "primary" | "success" | "warning" | "coral";
}) {
  const toneClass = {
    primary: "text-primary",
    success: "text-emerald-400",
    warning: "text-amber-300",
    coral: "text-accent"
  }[tone];
  return (
    <div className="rounded-xl border border-white/[0.09] bg-black/15 p-3">
      <strong className={cn("mono-data block text-lg font-black", toneClass)}>{value}</strong>
      <span className="mt-1 block text-[8px] font-bold uppercase tracking-wider text-white/45">{label}</span>
    </div>
  );
}

// ErrorState cung cấp thông điệp lỗi ngắn gọn và một hành động thử lại rõ ràng.
export function ErrorState({
  title,
  description,
  action
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <Card className="mx-auto max-w-xl p-8 text-center">
      <h2 className="text-xl font-black text-destructive">{title}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{description}</p>
      {action ? <div className="mt-5">{action}</div> : null}
    </Card>
  );
}

// EmptyState thống nhất bố cục cho các danh sách chưa có dữ liệu.
export function EmptyState({
  icon,
  title,
  description,
  action
}: {
  icon: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <Card className="relative overflow-hidden p-10 text-center md:p-14">
      <div className="data-grid-bg pointer-events-none absolute inset-0 opacity-45" />
      <div className="relative">
        <span className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">{icon}</span>
        <h2 className="mt-5 text-xl font-black">{title}</h2>
        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-muted-foreground">{description}</p>
        {action ? <div className="mt-5">{action}</div> : null}
      </div>
    </Card>
  );
}

// Dialog xác nhận dùng primitive trình duyệt để giữ focus trap và phím Escape đúng chuẩn.
export function ConfirmDangerDialog({
  open,
  title,
  description,
  confirmLabel,
  busy = false,
  onCancel,
  onConfirm
}: {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
    }
    if (!open && dialog.open) {
      if (typeof dialog.close === "function") dialog.close();
      else dialog.removeAttribute("open");
    }
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      className="confirm-dialog m-auto w-[min(92vw,28rem)] rounded-[18px] border border-border bg-card p-0 text-card-foreground shadow-2xl backdrop:bg-[#071a17]/75 backdrop:backdrop-blur-sm"
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onCancel();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <div className="p-6 md:p-7">
        <span className="inline-flex rounded-full border border-destructive/25 bg-destructive/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-destructive">
          Thao tác không thể hoàn tác
        </span>
        <h2 id={titleId} className="mt-4 text-xl font-black tracking-tight">{title}</h2>
        <p id={descriptionId} className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button variant="outline" disabled={busy} onClick={onCancel} autoFocus>Hủy</Button>
          <Button variant="danger" disabled={busy} onClick={onConfirm}>
            {busy ? "Đang xử lý…" : confirmLabel}
          </Button>
        </div>
      </div>
    </dialog>
  );
}

type SwitchProps = {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  "aria-label": string;
};

export function Switch(props: SwitchProps) {
  return (
    <SwitchPrimitive.Root
      checked={props.checked}
      onCheckedChange={props.onCheckedChange}
      aria-label={props["aria-label"]}
      className="relative h-7 w-12 rounded-full border border-border bg-muted shadow-inner outline-none transition-colors data-[state=checked]:border-primary data-[state=checked]:bg-primary focus-visible:ring-2 focus-visible:ring-ring"
    >
      <SwitchPrimitive.Thumb className="block size-5 translate-x-1 rounded-full bg-white shadow-sm transition-transform data-[state=checked]:translate-x-6" />
    </SwitchPrimitive.Root>
  );
}

export const Menu = DropdownMenu.Root;
export const MenuTrigger = DropdownMenu.Trigger;

export function MenuContent({
  className,
  ...props
}: DropdownMenu.DropdownMenuContentProps) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        sideOffset={8}
        align="end"
        className={cn(
          "menu-content z-50 min-w-56 rounded-2xl border border-border/80 bg-card/95 p-1.5 text-card-foreground shadow-2xl backdrop-blur-xl",
          className
        )}
        {...props}
      />
    </DropdownMenu.Portal>
  );
}

export function MenuItem({
  className,
  ...props
}: DropdownMenu.DropdownMenuItemProps) {
  return (
    <DropdownMenu.Item
      className={cn(
        "flex min-h-10 cursor-default select-none items-center gap-2 rounded-xl px-3 text-sm font-semibold outline-none data-[highlighted]:bg-muted",
        className
      )}
      {...props}
    />
  );
}
